import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import apiService from '../../services/ApiService';
import type { Vehicle } from '../../types/vehicle';

export interface OwnerVehicleOption {
  id: number;
  label: string;
  subtitle?: string;
}

interface OwnerVehicleComboboxProps {
  selected: OwnerVehicleOption | null;
  onSelect: (vehicle: OwnerVehicleOption | null) => void;
  /** Vehicle IDs already linked to this club (hidden from results). */
  excludeIds?: number[];
  disabled?: boolean;
  inputClassName?: string;
}

function vehicleLabel(v: Vehicle): string {
  const nickname = (v.nickname || v.display_name || '').trim();
  const ymm = [v.year, v.make, v.model, v.trim].filter(Boolean).join(' ').trim();
  if (nickname && ymm && nickname.toLowerCase() !== ymm.toLowerCase()) {
    return `${nickname} · ${ymm}`;
  }
  return nickname || ymm || `Vehicle #${v.id}`;
}

function vehicleSubtitle(v: Vehicle): string | undefined {
  const bits: string[] = [];
  const slug = (v as Vehicle & { public_slug?: string }).public_slug || v.slug;
  if (slug) bits.push(`/${slug}`);
  if (v.is_public === false) bits.push('Private');
  return bits.length ? bits.join(' · ') : undefined;
}

function toOption(v: Vehicle): OwnerVehicleOption {
  return {
    id: v.id,
    label: vehicleLabel(v),
    subtitle: vehicleSubtitle(v),
  };
}

function matchesQuery(v: Vehicle, q: string): boolean {
  const slug = (v as Vehicle & { public_slug?: string }).public_slug || v.slug;
  const hay = [String(v.id), v.nickname, v.display_name, v.year, v.make, v.model, v.trim, slug]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return hay.includes(q.toLowerCase());
}

/**
 * Search/select from the signed-in user's garage — used where a raw vehicle ID is unusable.
 */
const OwnerVehicleCombobox: React.FC<OwnerVehicleComboboxProps> = ({
  selected,
  onSelect,
  excludeIds = [],
  disabled = false,
  inputClassName = 'input flex-1',
}) => {
  const listboxId = useId();
  const inputId = useId();
  const [query, setQuery] = useState('');
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const exclude = useMemo(() => new Set(excludeIds.filter((id) => id > 0)), [excludeIds]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiService
      .getUserVehicles()
      .then((list) => {
        if (cancelled) return;
        setVehicles(Array.isArray(list) ? list : []);
        setLoadError(null);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setVehicles([]);
        setLoadError(e instanceof Error ? e.message : 'Could not load your garage');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) {
        setOpen(false);
        setHighlight(-1);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const options = useMemo(() => {
    const available = vehicles.filter((v) => !exclude.has(v.id));
    const q = query.trim();
    const filtered = q ? available.filter((v) => matchesQuery(v, q)) : available;
    return filtered.slice(0, 12).map(toOption);
  }, [vehicles, exclude, query]);

  useEffect(() => {
    setHighlight(options.length > 0 ? 0 : -1);
  }, [options]);

  const handleSelect = (opt: OwnerVehicleOption) => {
    onSelect(opt);
    setQuery('');
    setOpen(false);
    setHighlight(-1);
  };

  const handleClear = () => {
    onSelect(null);
    setQuery('');
    inputRef.current?.focus();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      setOpen(true);
      return;
    }
    if (e.key === 'Escape') {
      setOpen(false);
      setHighlight(-1);
      return;
    }
    if (!open || options.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => (h + 1) % options.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => (h <= 0 ? options.length - 1 : h - 1));
    } else if (e.key === 'Enter' && highlight >= 0 && options[highlight]) {
      e.preventDefault();
      handleSelect(options[highlight]);
    }
  };

  if (selected) {
    return (
      <div className="rounded-xl border border-emerald-800/40 bg-emerald-950/20 px-4 py-3 flex-1 min-w-0">
        <p className="text-[10px] uppercase tracking-wider text-emerald-500/80 mb-1">Selected build</p>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="font-medium text-white truncate">{selected.label}</p>
            {selected.subtitle ? <p className="text-sm text-gray-400 truncate">{selected.subtitle}</p> : null}
          </div>
          <button
            type="button"
            onClick={handleClear}
            disabled={disabled}
            className="text-sm text-gray-400 hover:text-white min-h-[40px] px-2 shrink-0"
          >
            Change
          </button>
        </div>
      </div>
    );
  }

  return (
    <div ref={rootRef} className="relative flex-1 min-w-0">
      <label htmlFor={inputId} className="input-label">
        Search your garage
      </label>
      <input
        ref={inputRef}
        id={inputId}
        type="search"
        role="combobox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-autocomplete="list"
        aria-activedescendant={
          highlight >= 0 && options[highlight] ? `${listboxId}-opt-${options[highlight].id}` : undefined
        }
        value={query}
        disabled={disabled || loading}
        placeholder={loading ? 'Loading your builds…' : 'Type year, make, model, or nickname…'}
        autoComplete="off"
        className={inputClassName}
        onChange={(e) => {
          setQuery(e.target.value);
          onSelect(null);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
      />
      {loadError ? <p className="mt-1 text-xs text-red-400">{loadError}</p> : null}
      {open && !loading && !loadError ? (
        <div
          id={listboxId}
          role="listbox"
          className="absolute z-40 mt-1 w-full max-h-64 overflow-y-auto rounded-xl border border-white/[0.08] bg-[#0a0a0a] shadow-xl"
        >
          {options.length === 0 ? (
            <p className="px-4 py-3 text-sm text-gray-500">
              {vehicles.length === 0
                ? 'No builds in your garage yet'
                : query.trim()
                  ? 'No matching builds'
                  : 'All of your garage builds are already linked'}
            </p>
          ) : (
            options.map((opt, i) => (
              <button
                key={opt.id}
                id={`${listboxId}-opt-${opt.id}`}
                type="button"
                role="option"
                aria-selected={i === highlight}
                className={`w-full text-left px-4 py-3 min-h-[48px] border-b border-white/[0.06] last:border-0 ${
                  i === highlight ? 'bg-[#141414]' : 'hover:bg-white/[0.05]'
                }`}
                onMouseEnter={() => setHighlight(i)}
                onClick={() => handleSelect(opt)}
              >
                <p className="font-medium text-white">{opt.label}</p>
                {opt.subtitle ? <p className="text-sm text-gray-400">{opt.subtitle}</p> : null}
              </button>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
};

export default OwnerVehicleCombobox;
